import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { ProfileTrackerState } from "@/types/profileTracker";

import { RootState } from "@/redux";

import { Order } from "@/types/types";

import { People } from "../../pages/Apollo/schema/get-people";
import { TrackerPerson } from "../../pages/ProfileTracker/schema/trackerPerson";
import { profileTrackerApi } from "./profileTracker.api";

type DeepResearchResponseData = NonNullable<People["deepResearchResponse"]>["response"];

const initialState: ProfileTrackerState = {
	profileTracker: [],
	profileTrackerMeta: {
		page: 1,
		limit: 15,
		itemCount: 15,
		pageCount: 1,
		hasPreviousPage: false,
		hasNextPage: false,
	},
	sortingProfileTracker: {
		sortBy: "updatedAt",
		sortOrder: Order.DESC,
	},
	records: [],
	recordsMeta: {
		page: 1,
		limit: 15,
		itemCount: 15,
		pageCount: 1,
		hasPreviousPage: false,
		hasNextPage: false,
	},
	sortingRecords: {
		sortBy: "updatedAt",
		sortOrder: Order.DESC,
	},
	resetColumns: false,
};

export const profileTrackerSlice = createSlice({
	name: "profileTracker",
	initialState,
	reducers: {
		setProfileTrackerSorting: (state, { payload }) => {
			state.sortingProfileTracker = payload;
		},
		setRecordsSorting: (state, { payload }) => {
			state.sortingRecords = payload;
		},
		clearProfileTracker: (state) => {
			state.profileTracker = [];
		},
		clearRecords: (state) => {
			state.records = [];
		},
		finalizeResetColumns: (state) => {
			state.resetColumns = false;
		},
		removeTrackerPersonFromRecord: (
			state,
			{ payload }: PayloadAction<{ organizationId: number; personId: number }>
		) => {
			const recordIndex = state.records.findIndex((r) => r.id === payload.organizationId);

			if (recordIndex !== -1 && state.records[recordIndex].trackerPersons) {
				const personIndex = state.records[recordIndex].trackerPersons!.findIndex(
					(p) => p.id === payload.personId
				);

				if (personIndex !== -1) {
					state.records[recordIndex].trackerPersons!.splice(personIndex, 1);
				}
			}
		},
		updateTrackerPersonsInRecord: (
			state,
			{ payload }: PayloadAction<{ organizationId: number; trackerPersons: TrackerPerson[] }>
		) => {
			const recordIndex = state.records.findIndex((r) => r.id === payload.organizationId);

			if (recordIndex !== -1) {
				state.records[recordIndex].trackerPersons = payload.trackerPersons;
			}
		},
		updateDeepResearchResponseInRecords: (
			state,
			{
				payload,
			}: PayloadAction<{
				prospectId: number;
				deepResearchResponseId: number;
				response: DeepResearchResponseData;
				fitAssessment?: string | null;
				fitReason?: string | null;
			}>
		) => {
			for (const record of state.records) {
				if (!record.trackerPersons) {
					continue;
				}

				for (const trackerPerson of record.trackerPersons) {
					if (trackerPerson.prospectId === payload.prospectId) {
						trackerPerson.prospect.deepResearchResponse = {
							id: payload.deepResearchResponseId,
							response: payload.response,
							fitAssessment: payload.fitAssessment ?? null,
							fitReason: payload.fitReason ?? null,
						};
					}
				}
			}
		},
	},
	extraReducers: (builder) => {
		builder.addMatcher(
			profileTrackerApi.endpoints.getProfileTrackers.matchFulfilled,
			(state, { payload }) => {
				if (!payload) {
					return;
				}

				if (payload.data) {
					state.profileTracker = payload.data;
				}

				if (payload.meta) {
					state.profileTrackerMeta = payload.meta;
				}
			}
		);
		builder.addMatcher(
			profileTrackerApi.endpoints.getTrackerOrganizations.matchFulfilled,
			(state, { payload }) => {
				if (!payload) {
					return;
				}

				if (payload.data) {
					state.records = payload.data;
				}

				if (payload.meta) {
					state.recordsMeta = payload.meta;
				}
			}
		);
		builder.addMatcher(
			profileTrackerApi.endpoints.updateTracker.matchFulfilled,
			(state, action) => {
				if (!action.payload.id) {
					return;
				}

				const index = state.profileTracker.findIndex((tracker) => tracker.id === action.payload.id);

				if (index === -1) {
					return;
				}

				if (action.payload.id) {
					state.profileTracker[index] = {
						...state.profileTracker[index],
						...action.payload,
					};
				}
			}
		);
		builder.addMatcher(
			profileTrackerApi.endpoints.updateColumnValue.matchFulfilled,
			(state, action) => {
				const index = state.records.findIndex(
					(trackerOrganization) => trackerOrganization.id === action.payload.id
				);

				const trackerOrganization = state.records[index];

				if (index === -1) {
					return;
				}

				const columnIndex = trackerOrganization.columns.findIndex(
					(column) => column.columnConfigId === action.payload.columnValue.columnConfigId
				);

				if (columnIndex > -1) {
					const column = trackerOrganization.columns[columnIndex];

					column.value = action.payload.columnValue.value;

					state.records[index].columns[columnIndex] = column;
				} else {
					state.records[index].columns.push(action.payload.columnValue);
				}
			}
		);
		builder.addMatcher(profileTrackerApi.endpoints.addColumnToTracker.matchFulfilled, (state) => {
			state.resetColumns = true;
		});
		builder.addMatcher(profileTrackerApi.endpoints.deleteColumn.matchFulfilled, (state) => {
			state.resetColumns = true;
		});
	},
});

export const {
	setProfileTrackerSorting,
	clearProfileTracker,
	clearRecords,
	setRecordsSorting,
	finalizeResetColumns,
	removeTrackerPersonFromRecord,
	updateTrackerPersonsInRecord,
	updateDeepResearchResponseInRecords,
} = profileTrackerSlice.actions;
export const profileTrackerReducer = profileTrackerSlice.reducer;
export const profileTrackerSelector = (state: RootState) => state[profileTrackerSlice.name];
